import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { data, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  createBot,
  getBotIdFromDB,
  initiateScoring,
  leaveMeetingBot,
  sendTestQueueJob,
  updateScoring,
} from '../../../../services/api';
import Breadcrumbs from '../../../../coreComponents/breadcrumbs';
import { Button, Card, CardBody, CardHeader, Col, Container, Row } from 'reactstrap';
import CommonCardHeader from '../../../../coreComponents/CommonCardHeader';
import { Image } from '../../../../coreComponents/Image';
import { AssistantExamResponse, ExamFile, ScoringData } from '../../../../types/Exams';
import QuestionList from './QuestionList';
import Swal from 'sweetalert2';
import { Primary } from '../../../../constants';
import { useAppSelector } from '../../../../reduxToolkit/hooks';
import { UserRole } from '../../../../types/Forms';
import { baseLang } from '../../../../lib/utils';
import { getExamsCreatedList } from '../../../../data/formsAndTable/tables/dataTables/DataSource';
import { getSocketSessionId, useSocket } from '../../../../hooks/useSocket';
import SimpleModal from '../../../uiKits/modal/basicModal/SimpleModal';
import ScoringDetails from './ScoringDetails';
import {
  useAssistantCommentExamMutation,
  useAssistantDeleteAssistantExamMutation,
  useAssistantGetExamDetailsQuery,
} from '../../../../reduxToolkit/reducers/usersSlice';
import { parseQuestionsField, parseRawResponse } from '../../../../lib/questionsParse';
import { formatWithOffset } from '../../../../lib/timeUtils';
import ExamTranscriptions from './ExamTranscriptions';
import ExamScoring from './ExamScoring';
import PassedExam from './PassedExam';
import CommentFileModal, { PreviewItem } from '../../../commetFileModal';
import { validateImages } from '../../../../lib/filesUtils';
import { usePreviews } from '../../../../hooks/usePreviews';
import { ModalDetails } from '../../../../types/Scoring';
import Loader from '../../../../layout/Loader';
import Error404 from '../../../pages/others/errorPage/Error404';
import { routes } from '../../../../routers/routes';
import { isNewPreview } from '../../../../utils';
import axios from 'axios';
import TestQueue from './TestQueue';

const ExamDetails = () => {
  const { role, id: userId } = useAppSelector((state) => state.auth);
  const { id } = useParams<{ id: string }>();
  const { t, i18n } = useTranslation();
  const pageLang = baseLang(i18n.language);
  const [examData, setExamData] = useState<AssistantExamResponse | null>(null);
  const [openModal, setOpenModal] = useState(false);
  const [comment, setComment] = useState('');
  const [isExamPassed, setIsExamPassed] = useState<boolean | null>(null);
  const [examId, setExamId] = useState<number | null>(null);
  const [currentDetailsId, setCurrentDetailsId] = useState<number | null>(null);
  const [uploadErrors, setUploadErrors] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const [parsedQuestions, setParsedQuestions] = useState<
    | {
        questions: string[];
        type: 'stringArray';
      }
    | {
        type: 'objectArray';
        questions: { questionNum: number; questionText: string }[];
      }
    | null
  >(null);
  const [accordionParsedQuestions, setAccordionParsedQuestions] = useState<
    {
      id: number;
      questions: string[] | null;
    }[]
  >([
    {
      id: 0,
      questions: null,
    },
  ]);
  const [botId, setBotId] = useState('');
  const [botLoading, setBotLoading] = useState(false);
  const botLsKey = id ? `attendeeBotId:${id}` : null;
  const [isOpenScoringDetails, setIsOpenScoringDetails] = useState(false);
  const [wasDeleted, setWasDeleted] = useState(false);
  const [modalDetails, setModalDetails] = useState<ModalDetails | null>(null);

  const [langSelected, setLangSelected] = useState('uk');
  const [toDeleteFileUrl, setToDeleteFileUrl] = useState<string[] | null>(null);

  const navigate = useNavigate();
  const location = useLocation();
  const {
    data: examDetailsData,
    isError,
    isLoading,
    isFetching,
  } = useAssistantGetExamDetailsQuery(id, { skip: wasDeleted });
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const commentRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const [commentHandler] = useAssistantCommentExamMutation();
  const [deleteAssistantExamHandler] = useAssistantDeleteAssistantExamMutation();
  const makeAbs = useCallback(
    (rel?: string) =>
      rel ? `${process.env.REACT_APP_BACKEND_STATIC}/crm/${rel}` : 'data:image/gif;base64,R0lG...',
    [],
  );

  const { previews, addFiles, removePreview, clearNonExisting, newFiles } = usePreviews(
    examData?.details?.files,
    makeAbs,
  );

  useEffect(() => {
    if (!botLsKey) return;
    try {
      const stored = localStorage.getItem(botLsKey);
      if (stored) setBotId(stored);
    } catch {}
  }, [botLsKey]);
  useEffect(() => {
    if (!examDetailsData) return;
    setLangSelected(examDetailsData.examLang);
    const item: AssistantExamResponse | null = Array.isArray(examDetailsData)
      ? (examDetailsData[0] ?? null)
      : (examDetailsData ?? null);

    setExamData(item);

    // current exam
    const parsed = parseQuestionsField(item?.questions ?? '');
    if (
      parsed &&
      (parsed.type === 'stringArray' || parsed.type === 'objectArray') &&
      typeof parsed.questions !== 'undefined' &&
      typeof parsed.type === 'string'
    ) {
      setParsedQuestions(
        parsed as
          | { questions: string[]; type: 'stringArray' }
          | { type: 'objectArray'; questions: { questionNum: number; questionText: string }[] },
      );
    } else {
      setParsedQuestions(null);
    }

    // attempts (exclude current exam id)
    const attempts = item?.assistant?.attempts ?? [];
    const accordionItemsWithQuestions = attempts
      .filter((att) => att.id !== Number(id))
      .map((att) => {
        const p = parseQuestionsField(att.questions ?? '');
        return { id: att.id, questions: p.questions, intro: p.intro };
      });

    // setAccordionParsedQuestions(accordionItemsWithQuestions);
  }, [examDetailsData, id]);

  const connectToMeeting = (data: { userId: number; meetLink: string; botName: string }) => {
    createMeetBot(data);
  };
  const createMeetBot = async (data: { userId: number; meetLink: string; botName: string }) => {
    const sessionId = getSocketSessionId();
    setBotLoading(true);
    const botData = {
      meetLink: data.meetLink,
      botName: data.botName,
      botLang: langSelected,
      user: data.userId,
      examId: id,
      sessionId,
    };
    const bot = await createBot(botData);
    if (bot && !bot.ok) {
      const reasonKey = bot.reason;

      const reasonTxt = t(`bot.error.${reasonKey}`);

      const swalIcon = reasonKey === 'transcription_in_progress' ? 'warning' : 'error';

      Swal.fire({ title: t('global.error'), text: reasonTxt, icon: swalIcon });
    }
    if (bot && bot.ok) {
      setBotId(bot.id);
      if (botLsKey) {
        try {
          localStorage.setItem(botLsKey, bot.id);
        } catch {}
      }
      Swal.fire({
        text: t('bot.botCreatedTitle'),
        confirmButtonText: 'Ok',
        confirmButtonColor: Primary,
      });
    }
    setBotLoading(false);
  };

  const leaveMeeting = async () => {
    let currentBotId = botId;
    if (!currentBotId && botLsKey) {
      try {
        const stored = localStorage.getItem(botLsKey);
        if (stored) currentBotId = stored;
      } catch {}
    }

    if (!currentBotId) {
      const res = await getBotIdFromDB(id);
      if (res) {
        currentBotId = res.botId;
      } else {
        Swal.fire({
          text: t('bot.noBotToLeave'),
          confirmButtonText: 'Ok',
          confirmButtonColor: Primary,
        });
        return;
      }
    }

    const leave = await leaveMeetingBot(currentBotId);
    if (leave) {
      setBotId('');
      if (botLsKey) {
        try {
          localStorage.removeItem(botLsKey);
        } catch {}
      }
      Swal.fire({
        text: t('bot.botLeftMeeting'),
        confirmButtonText: 'Ok',
        confirmButtonColor: Primary,
      });
    }
  };

  const handleEdit = () => {
    if (examData) {
      const fileId = examData.callTranscriptionId?.split('.').slice(0, -1).join('.');
      navigate(`/exams/bot/${fileId}`, {
        state: { from: location.pathname },
      });
    }
  };

  const deleteExamHandler = async () => {
    try {
      const payload = {
        examId: Number(id!),
        userId: examData!.user.id,
      };
      const res = await deleteAssistantExamHandler(payload).unwrap();
      if (res.success) {
        setWasDeleted(true);
        navigate('/exams/exam-list');
        Swal.fire({
          title: t('courses.exam.calendar.eventDeleteTitle'),
          text: t('courses.exam.calendar.eventDeleteText'),
          icon: 'success',
          confirmButtonColor: Primary,
        });
      }
    } catch (error) {
      Swal.fire({
        title: 'Error!',
        text: 'Exam delete failed',
        icon: 'error',
        confirmButtonColor: Primary,
      });
    }
  };

  const alertDelete = () => {
    Swal.fire({
      title: t('exams.removeExamAlertTitle'),
      text: t('exams.removeExamAlertText'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: Primary,
    }).then((result) => {
      if (result.isConfirmed) deleteExamHandler();
      else Swal.fire({ text: t('exams.removeExamDeclineNotification') });
    });
  };

  const goToExamHandler = (id: number) => {
    if (isNaN(Number(id))) return false;
    navigate(`/exams/details/${id}`);
  };
  const handleInitiateScoring = async () => {
    try {
      const sessionId = getSocketSessionId();
      const payload = {
        assistantExamId: examData?.id,
        sessionId,
      };
      const res = await initiateScoring(payload);
      if (!res.success) {
        Swal.fire({
          title: t('scoring.scoringInitErrorTitle'),
          text: t('scoring.scoringInitErrorNoDataText'),
          icon: 'warning',
          showConfirmButton: true,
          confirmButtonText: 'Ok',
        });
      } else if (res.success && res.inProgress) {
        Swal.fire({
          title: t('scoring.swalScoringInProgress'),
          text: t('scoring.swalScoringInProgressText'),
          icon: 'warning',
          showConfirmButton: true,
        });
      } else if (res.success) {
        Swal.fire({
          title: t('scoring.scoringInitSuccessTitle'),
          text: t('scoring.scoringInitSuccessText'),
          icon: 'success',
          showConfirmButton: true,
          confirmButtonText: 'Ok',
        });
      }
    } catch (error) {
      Swal.fire({
        title: t('scoring.scoringInitErrorTitle'),
        text: t('scoring.scoringInitErrorElseText'),
        icon: 'warning',
        showConfirmButton: true,
        confirmButtonText: 'Ok',
      });
    }
  };

  const handleUpdateScore = async (editedScore: number) => {
    try {
      const sessionId = getSocketSessionId();
      const payload = {
        assistantExamId: examData?.id,
        sessionId,
        newScore: editedScore,
      };
      const res = await updateScoring(payload);
      if (res) {
        setExamData((prev) => {
          if (!prev) return prev;
          if (!prev.scoring) return prev;
          return {
            ...prev,
            scoring: {
              ...(prev.scoring ?? {}),
              score: editedScore,
            },
          };
        });
      }
    } catch (error) {
      Swal.fire({
        title: t('scoring.updScoreErr'),
        icon: 'warning',
      });
    }
  };
  const viewerTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const handleModalOpen = (
    exam_id: number,
    examComment: string,
    detailsId: number | null,
    existingFiles?: ExamFile[] | null | undefined,
  ) => {
    setExamId(exam_id);
    setComment(examComment ?? '');
    setIsExamPassed(examData?.details?.examPassed ?? null);
    setCurrentDetailsId(detailsId);
    setUploadErrors([]);
    setOpenModal(true);
  };

  const revokeObjectUrls = (arr: PreviewItem[]) => {
    arr.forEach((p) => {
      if (isNewPreview(p)) URL.revokeObjectURL(p.url);
    });
  };

  const handleModalClose = () => {
    revokeObjectUrls(previews);
    setOpenModal(false);
    setComment('');

    setUploadErrors([]);
  };

  const handleFilesSelected: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const files = Array.from(e.target.files ?? []);
    if (!files.length) return;
    const { valid, errors } = validateImages(files, t);
    setUploadErrors(errors);
    if (valid.length) addFiles(valid);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmitComment = async () => {
    if (!examId) return;
    const fd = new FormData();
    fd.append('examId', String(examId));
    fd.append('comment', comment);
    if (currentDetailsId != null) fd.append('currentDetailsId', String(currentDetailsId));
    newFiles.forEach((file) => fd.append('screenshots', file));
    fd.append('examPassed', String(isExamPassed));
    fd.append('toDeleteFileUrls', toDeleteFileUrl ? JSON.stringify(toDeleteFileUrl) : '[]');

    try {
      setSubmitting(true);
      await commentHandler(fd).unwrap();
      clearNonExisting(); // keep only existing after submit
      Swal.fire({
        title: t('leads.notifications.successTitle'),
        text: t('leads.notifications.successText'),
        icon: 'success',
      });
    } catch (error) {
      console.log('error', error);
      Swal.fire({
        title: t('leads.notifications.errorCommentTitle'),
        text: t('leads.notifications.errorUpdateText'),
        icon: 'error',
      });
    } finally {
      setSubmitting(false);
      setOpenModal(false);
    }
  };
  const handleRemovePreview = (p: PreviewItem) => {
    removePreview(p);
  };

  const handleCloseModal = () => {
    if (examData) {
      setComment(examData.details?.comment ?? '');
    }
    setOpenModal(false);
  };
  /** ——— CLEANUP ON UNMOUNT ——— */
  useEffect(() => {
    return () => {
      revokeObjectUrls(previews);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleScoringDetailsModalWindow = (row: ScoringData, key: 'tr' | 'q' | 'a' | 'p') => {
    switch (key) {
      case 'tr':
        const transcription = JSON.parse(row.scoringTranscription);
        setModalDetails({ kind: 'transcript', data: transcription });
        break;
      case 'a':
        const rawResp = parseRawResponse(row.rawResponse);
        const withAnswers = rawResp.perQuestion;
        setModalDetails({ kind: 'answers', data: withAnswers });
        break;
      case 'q':
        const rawRespQ = parseRawResponse(row.rawResponse);
        const questions = rawRespQ.perQuestion.map((el: any) => {
          return {
            index: el.index,
            question: el.question,
          };
        });
        setModalDetails({ kind: 'questions', data: questions });
        break;
      case 'p':
        const prompt = row.scoringPrompt;
        setModalDetails({ kind: 'prompt', data: prompt });
        break;
      default:
        break;
    }

    setIsOpenScoringDetails(true);
  };
  const handleDeleteFile = async (p: PreviewItem) => {
    Swal.fire({
      title: t('global.filesMutationsText.titleDelete'),
      text: t('global.filesMutationsText.textDelete'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: t('global.filesMutationsText.delete'),
      cancelButtonText: t('global.cancel'),
    }).then((result) => {
      if (result.isConfirmed) {
        setToDeleteFileUrl((prev) => (prev ? [...prev, p.url] : [p.url]));
        handleRemovePreview(p);
      } else if (result.isDismissed) {
        return;
      }
    });
  };

  if (isLoading || isFetching) return <Loader />;

  if (isError || (examDetailsData && !examDetailsData.id))
    return (
      <Error404 goto={routes.Exams.ExamList} buttonLabel={t('global.backButtonLabel.examList')} />
    );
  if (!examData) return <div>{t('global.noData')}</div>;

  return (
    <>
      <Breadcrumbs
        mainTitle={t('courses.exam.examDetailsPageTitle', { id: examData.id })}
        // parent={t('courses.generalTitle')}
      />
      <Container fluid>
        <Row>
          <Col sm="12">
            <Card>
              <Row>
                <Col sm="8">
                  {' '}
                  <CommonCardHeader title={t('courses.exam.examData')} tag />{' '}
                </Col>
                {role === UserRole.ADMIN && (
                  <Col sm="auto" className="pt-3 d-flex justify-content-end flex-grow-1">
                    <Button className="me-3" onClick={alertDelete}>
                      {t('exams.removeExam')}
                    </Button>
                  </Col>
                )}
              </Row>

              <CardBody>
                <Row>
                  <Col sm="2">
                    <Image
                      src={
                        examData.user?.photo
                          ? `${process.env.REACT_APP_BACKEND_STATIC}/userPics/${examData.user.photo}`
                          : '/assets/images/avtar/default-user.png'
                      } // or `data.lessonCoverPath`
                      alt="User photo"
                      className="img-fluid"
                      width={200}
                    />
                  </Col>
                  <Col sm="10">
                    {examData && (
                      <p className="mb-2">
                        {t('courses.exam.examForLesson', {
                          lesson:
                            pageLang === 'en'
                              ? examData.lesson.title
                              : (examData.lesson.translations.find((tr) => tr.lang === pageLang)
                                  ?.title ?? examData.lesson.title),
                        })}
                      </p>
                    )}
                    <p className="mb-2">
                      {t('courses.exam.course')}:{' '}
                      {pageLang === 'en'
                        ? examData.course.name
                        : (examData.course.translations.find((tr) => tr.lang === pageLang)?.name ??
                          examData.course.name)}{' '}
                    </p>
                    <p className="mb-2">
                      {t('courses.exam.calendar.eventCategory')}:{' '}
                      {pageLang === 'en'
                        ? examData.lesson.category.name
                        : (examData.lesson.category.translations.find((tr) => tr.lang === pageLang)
                            ?.name ?? examData.lesson.category.name)}{' '}
                    </p>

                    {/* <p>
                      {t('courses.exam.calendar.eventLesson')}:{' '}
                      {pageLang === 'en'
                        ? examData.lesson.title
                        : (examData.lesson.translations.find((tr) => tr.lang === pageLang)?.title ??
                          examData.lesson.title)}{' '}
                    </p> */}
                    <p className="mb-2">
                      {t('courses.exam.examDate')}:{' '}
                      {formatWithOffset(examData.examDate, viewerTz, undefined)}
                    </p>

                    <p className="mb-2">
                      {t('courses.exam.calendar.eventDescription')}:{' '}
                      {pageLang === 'en'
                        ? examData.lesson.description
                        : (examData.lesson.translations.find((tr) => tr.lang === pageLang)
                            ?.description ?? examData.lesson.description)}{' '}
                    </p>

                    <p className="mb-2">
                      {t('courses.exam.student')}: {examData.user.firstName}{' '}
                      {examData.user.lastName}
                    </p>
                    <p className="mb-2">Email: {examData.user.email}</p>

                    <p className="mb-2">
                      {t('courses.exam.examLanguage')}:{' '}
                      {examData.examLang === 'uk'
                        ? t('courses.exam.examLangUkrainian')
                        : t('courses.exam.examLangRussian')}
                    </p>
                    <div className="d-flex flex-column">
                      {role === UserRole.STUDENT && (
                        <p className="mr-3">
                          {t('bot.googleMeetLink')}
                          <a href={examData.googleMeetLink} target="_blank" rel="noreferrer">
                            {examData.googleMeetLink}
                          </a>
                        </p>
                      )}
                      {/* {role !== UserRole.STUDENT && (
                        <>
                          {examData.scoring && (
                            <Col sm="12" className="mt-3">
                              {' '}
                              <Card>
                                <CardBody>
                                  <h5 className="mb-3">{t('scoring.scoring')}</h5>
                                  <Row className="mb-3">
                                    <Col sm="4" className="d-flex gap-1 align-items-center">
                                      <p className="m-0">{t('scoring.score')}: </p>
                                      <p className="m-0 f-w-700 text-muted">
                                        {examData.scoring.score}
                                      </p>
                                      {examData?.scoring?.score && (
                                        <Button color="warning" outline tag="a" size="sm">
                                          <i className="fa fa-pencil" onClick={openEditScore}></i>
                                        </Button>
                                      )}
                                    </Col>
                                    <Col sm="4" className="d-flex gap-1">
                                      <p className="m-0">{t('scoring.scoringDate')}: </p>
                                      <p className="m-0 f-w-700 text-muted">
                                        {formatWithOffset(
                                          examData.scoring.createdAt,
                                          viewerTz,
                                          undefined,
                                        )}
                                      </p>
                                    </Col>
                                    <Col sm="4" className="d-flex gap-1">
                                      <p>{t('scoring.status')}:</p>
                                      <p className="f-w-700 text-muted">
                                        {examData.scoring.failedScoring
                                          ? t('scoring.fail')
                                          : t('scoring.success')}
                                      </p>
                                    </Col>
                                  </Row>{' '}
                                  {examData.scoring?.rawResponse && (
                                    <Col sm="12">
                                      <Button
                                        type="button"
                                        onClick={() => setIsOpenScoringDetails(true)}
                                      >
                                        {t('scoring.details')}
                                      </Button>
                                    </Col>
                                  )}
                                </CardBody>
                              </Card>
                            </Col>
                          )}
                        </>
                      )} */}
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
          {role !== UserRole.STUDENT && (
            <>
              <PassedExam
                examData={examData}
                role={role}
                botId={botId}
                connectToMeeting={() =>
                  connectToMeeting({
                    userId: examData.user.id,
                    meetLink: examData.googleMeetLink,
                    botName: 'AnanatraBot',
                  })
                }
                botLoading={botLoading}
                leaveMeeting={leaveMeeting}
                previews={previews}
                onRemovePreview={handleRemovePreview}
                handleModalOpen={handleModalOpen}
              />
              <Col sm="12">
                <Card>
                  <CommonCardHeader title={t('courses.exam.examParams')} tag />
                  <CardBody>
                    <Row>
                      <h5 className="mb-2 f-w-600">{t('courses.exam.usedPrompt')}</h5>
                      <p>{examData.prompt}</p>
                    </Row>
                    <Row>
                      <h5 className="mt-4 mb-2 f-w-600">{t('courses.exam.examQuestions')}</h5>
                      <QuestionList
                        qs={parsedQuestions ?? { type: 'stringArray', questions: [] }}
                      />
                    </Row>
                  </CardBody>
                </Card>
              </Col>
              <ExamTranscriptions
                callTranscriptionId={examData.callTranscriptionId}
                callAudioId={examData.callAudioId}
                handleEdit={handleEdit}
              />
              <ExamScoring
                examData={examData}
                handleInitiateScoring={handleInitiateScoring}
                handleScoringDetailsModalWindow={handleScoringDetailsModalWindow}
              />
            </>
          )}
        </Row>

        {modalDetails && (
          <SimpleModal
            size="xl"
            isOpen={isOpenScoringDetails}
            close={() => setIsOpenScoringDetails(false)}
          >
            <ScoringDetails {...modalDetails} />
          </SimpleModal>
        )}
      </Container>
      <CommentFileModal
        isOpen={openModal}
        onClose={() => handleCloseModal()}
        labels={{
          title: t('leads.commentForm.label'),
          placeholder: t('leads.commentForm.placeholder'),
          ok: t('global.ok'),
          saving: t('global.saving'),
          cancel: t('global.cancel'),
          remove: t('common.remove'),
          examHandled: t('leads.examHandled'),
          yes: t('exams.yes'),
          no: t('exams.no'),
        }}
        commentRef={commentRef as React.RefObject<HTMLTextAreaElement>}
        comment={comment}
        onCommentChange={setComment}
        hasFileInput={true}
        fileInputRef={fileInputRef as React.RefObject<HTMLInputElement>}
        onFilesSelected={handleFilesSelected}
        previews={previews}
        onRemovePreview={handleRemovePreview}
        uploadErrors={uploadErrors}
        submitting={submitting}
        onSubmit={handleSubmitComment}
        isExamPassed={isExamPassed}
        setIsExamPassed={setIsExamPassed}
        onFileDelete={handleDeleteFile}
      />
      {/* <SimpleModal
        isOpen={isOpenTranscriptModal}
        close={() => setIsOpenTranscriptModal(false)}
      ></SimpleModal> */}
    </>
  );
};

export default ExamDetails;
